import { constants } from "src/shared/constants";
import { useAuthLoginErrorModal } from "../components/GlobalModals/AuthLoginErrorModal";
import { findApiErrorMessage } from "../utils/utils";
import { useGlobalStore } from "./useGlobalStore";
import { toast } from "react-toastify";
import { useStorageCache } from "./useStorageCache";
import { useTeamConfig } from "../components/TeamConfigProvider";
import { useRouterExt } from "./useRouterExt";
import { jwtDecode } from "jwt-decode";
import { useApiRoutes } from "./useApiRoutes";
import {
  TOAuthState,
  TTempOAuthAccount,
  TUserData,
} from "src/shared/types/globalStotre.types";
import {
  TLoginApiPayload,
  TLoginApiResponse,
  TRegisterApiResponse,
  TUpdateProfile,
  TOktaTransactionStorage,
} from "src/shared/types/api.types";
import { global_config } from "src/shared/global_config";
import { routes } from "src/shared/routes";
import { useGenericModal } from "../components/GenericModal";
import { dispatchCustomEvent, throttleTime } from "src/utils/utils";

export function useSSAuth() {
  const { FRONTEND_URL } = global_config;
  const { TEAM_SCHEMA, VENDOR } = useTeamConfig();
  const {
    setGlobalState,
    session,
    oAuthState,
    tempOAuthAccount,
    mlbBallparkUser,
  } = useGlobalStore();
  const router = useRouterExt();
  const sessionStorage = useStorageCache(constants.storage.session);
  const isBallparkUser = Boolean(mlbBallparkUser?.mlbBallparkOktaId);

  // apis
  const { loginApi, registerApi, logoutApi, updateProfileApi } = useApiRoutes();

  // modals
  const authLoginErrorModal = useAuthLoginErrorModal();
  const genericModal = useGenericModal();

  // helper methods

  const persistSession = (session: string) => {
    const user = jwtDecode(session) as TUserData;
    setGlobalState({ session, user });
    sessionStorage.set(TEAM_SCHEMA, session, 1000 * 60 * 60 * 24 * 7);
  };

  const removeSession = () => {
    setGlobalState({ session: null, user: null });
    sessionStorage.delete(TEAM_SCHEMA);
  };

  const setTempOAuthAccount = (acc: TTempOAuthAccount) => {
    setGlobalState({ tempOAuthAccount: acc });
  };

  const fireManifestMessageModal = (message?: string, title?: string) => {
    return new Promise((resolve) => {
      let isResolved = false;
      genericModal.set({
        isOpen: true,
        title: title || "",
        description: message || "",
        isConfirmButtonHidden: true,
        hideTitleIcon: true,
        onClickClose: async () => {
          isResolved = true;
          resolve(true);
        },
      });
      throttleTime(5000, () => {
        if (isResolved) return;
        genericModal.set({ isOpen: false });
        resolve(true);
      });
    });
  };

  const handleLoginApiRegisterRes = async (
    loginRes: TLoginApiResponse,
    oAuthState: TOAuthState,
  ) => {
    const { authOrigins } = constants;
    setTempOAuthAccount(loginRes.register);
    if (oAuthState?.authOrigin === authOrigins.purchasePlan) {
      await router.pushSchema({
        pathname: routes.checkout,
        query: {
          authType: constants.authTypes.register,
          tab: constants.checkoutTabs.auth,
        },
      });
    } else {
      await router.pushSchema({
        pathname: routes.auth,
        query: { authType: constants.authTypes.register },
      });
    }
  };

  const handleLoginApiSessionRes = async (
    loginRes: TLoginApiResponse,
    oAuthState: TOAuthState,
  ) => {
    const session = loginRes?.session;
    const redirectRoute = oAuthState?.redirectRoute;
    if (session) persistSession(session);
    if (redirectRoute) await router.pushSchema(redirectRoute);
    else await router.pushSchema(routes.landing);
  };

  const handleRegisterSuccess = (registerRes: TRegisterApiResponse) => {
    registerRes.session && persistSession(registerRes.session);
    const redirectRoute = oAuthState?.redirectRoute || "";
    router.pushSchema(redirectRoute);
  };

  const handleLoginError = (
    error: any,
    options?: {
      disableLogout: boolean;
    },
  ) => {
    let tmAccountDetails: any = {};
    if (error?.email) tmAccountDetails.email = error.email;
    if (error?.accountId) tmAccountDetails.accountId = error.accountId;
    if (tmAccountDetails) {
      authLoginErrorModal.set({
        isOpen: true,
        errorMessage: findApiErrorMessage(error),
        tmAccountDetails,
        disableLogout: options?.disableLogout || false,
      });
    } else {
      authLoginErrorModal.set({
        isOpen: true,
        errorMessage: findApiErrorMessage(error),
      });
    }
  };

  const getUserData = (): TUserData | null => {
    if (!session) return null;
    const user = jwtDecode(session);
    return user as TUserData;
  };

  const loginToSs = async () => {
    try {
      const code = String(router?.query?.code);
      let redirectUri = `${FRONTEND_URL}/tmcallback/${TEAM_SCHEMA}`;
      let loginPayload: TLoginApiPayload = { tmAuth: { code, redirectUri } };
      const state = router?.query?.state as string;

      if (VENDOR === constants.vendorUUIDs.seatgeek)
        loginPayload = {
          seatGeekAuth: { code },
        };

      if (VENDOR === constants.vendorUUIDs.ticketsdotcom) {
        if (isBallparkUser) {
          loginPayload = {
            tdcAuth: {
              mlbBallparkOktaId: mlbBallparkUser?.mlbBallparkOktaId,
              mlbBallparkEmail: mlbBallparkUser?.mlbBallparkEmail,
            },
          };
        } else {
          const rawTransaction = window.localStorage.getItem("okta-shared-transaction-storage") as string; // prettier-ignore
          if (!code || !rawTransaction) return;

          let oktaTransactionStorage: TOktaTransactionStorage;

          try {
            oktaTransactionStorage = JSON.parse(rawTransaction);
          } catch (error) {
            console.log(error);
            return;
          }

          const { codeVerifier, state: oktaSessionState, nonce } = oktaTransactionStorage[state].transaction; // prettier-ignore
          redirectUri = `${FRONTEND_URL}/mlbcallback?codeVerifier=${codeVerifier}&oktaSessionState=${oktaSessionState}&nonce=${nonce}`;
          loginPayload = { tdcAuth: { code, redirectUri } };
        }
      }

      const oAuthState = JSON.parse(window.atob((state) || "") || "null") as TOAuthState // prettier-ignore
      if ((!code || !oAuthState) && !isBallparkUser) return;
      setGlobalState({ oAuthState: oAuthState });
      const loginRes = await loginApi.post(loginPayload);

      // broadcasting the login api success event
      dispatchCustomEvent(constants.customEvents.loginApiSuccess, {
        loginRes,
        state: oAuthState,
      });

      if (loginRes?.error) return handleLoginError(loginRes.error);
      if (loginRes?.register) handleLoginApiRegisterRes(loginRes, oAuthState);
      if (loginRes?.session) {
        const manifestMessage = loginRes?.manifestUpdateMessage;
        if (manifestMessage) await fireManifestMessageModal(manifestMessage);
        await handleLoginApiSessionRes(loginRes, oAuthState);
      }
    } catch (error) {
      handleLoginError(error);
    }
  };

  const registerToSs = async (formData: {
    phoneNumber: string;
    firstName?: string;
    lastName?: string;
  }) => {
    if (!tempOAuthAccount) return;
    const { phoneNumber, firstName, lastName } = formData;
    const { phoneNumber: ph, ...restAccountDetails } = tempOAuthAccount;

    try {
      const regPayload = {
        firstName,
        lastName,
        phoneNumber,
        ...restAccountDetails,
      };
      const registerRes = await registerApi.post(regPayload);

      if (registerRes?.error) handleLoginError(registerRes?.error);

      const session = registerRes.session;

      if (session) {
        const manifestMessage = registerRes?.manifestUpdateMessage;
        if (manifestMessage) {
          await fireManifestMessageModal(
            manifestMessage,
            "Registration Complete!",
          );
        }
        handleRegisterSuccess(registerRes);
      }
    } catch (error) {
      handleLoginError(error, {
        disableLogout: true,
      });
    }
  };

  const logoutFromSs = async () => {
    try {
      await logoutApi.get();
      removeSession();
    } catch (err) {
      console.log(err);
    }
  };

  const updateProfile = async ({
    lastName,
    firstName,
    phoneNumber,
  }: TUpdateProfile) => {
    try {
      const updateProfileRes = await updateProfileApi.post({
        lastName,
        firstName,
        phoneNumber,
      });
      if (updateProfileRes?.session) persistSession(updateProfileRes.session);
    } catch (error) {
      authLoginErrorModal.set({
        isOpen: true,
        errorMessage: findApiErrorMessage(error),
      });
    }
  };

  return {
    user: getUserData(),
    loginToSs,
    registerToSs,
    updateProfileApi,
    persistSession,
    logoutFromSs,
    getUserData,
    updateProfile,
  };
}
